import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type {
  HolidaysResponse,
  LoadHolidaysRequestInput,
  OrganizationCalendarInput,
  OrganizationCalendarResponse,
  OrganizationIdentity,
  PreviewOccurrencesRequestInput,
  PreviewOccurrencesResponse,
  UpdateOrganizationRequest,
} from "@opus-dt/shared-types";
import { api } from "@/lib/api-client";

// Queries e mutations da organização.
// INVALIDAÇÃO (guardrail #12): o expediente da fábrica é o denominador da Disponibilidade de toda
// linha que o herda. Salvar aqui muda indicador em escala de fábrica, retroativamente — e o mesmo
// vale para o FUSO, que desloca as janelas reais de expediente. Por isso as duas mutations invalidam
// o MESMO conjunto amplo.
// Sem `lineId` nas chaves: o prefixo cobre todas as linhas. Uma chave por linha exigiria saber
// quais herdam, que é justamente o que a resolução de camadas decide do lado do servidor.

const organizationKey = ["organization"] as const;
const calendarKey = ["organization", "calendar"] as const;

export function useOrganization() {
  return useQuery<OrganizationIdentity>({
    queryKey: organizationKey,
    queryFn: () => api.get<OrganizationIdentity>("/api/v1/organization"),
  });
}

export function useOrganizationCalendar() {
  return useQuery<OrganizationCalendarResponse>({
    queryKey: calendarKey,
    queryFn: () => api.get<OrganizationCalendarResponse>("/api/v1/organization/calendar"),
  });
}

function useInvalidateOrganization() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: organizationKey });
    // As linhas passam a resolver outro expediente — as que herdam mudam de tempo planejado.
    void qc.invalidateQueries({ queryKey: ["lines"] });
    void qc.invalidateQueries({ queryKey: ["oee"] });
    void qc.invalidateQueries({ queryKey: ["dashboard"] });
    // Os indicadores da OP saem do mesmo `calculate_oee` que o card da linha.
    void qc.invalidateQueries({ queryKey: ["production-orders"] });
    // O preview do cenário herda o calendário da linha base, que agora pode vir da fábrica.
    void qc.invalidateQueries({ queryKey: ["scenarios"] });
    // ── Spec 079 (T054) — a POLÍTICA DE JULGAMENTO atravessa o painel inteiro ──────────────────
    //
    // Mudar um dos dez parâmetros não muda um número: muda o que a plataforma AFIRMA sobre todos
    // eles de uma vez. A régua do gêmeo é reclassificada inteira (outro limiar de acurácia, outra
    // amostra mínima), as cores do OEE mudam em toda linha, os vereditos de aderência mudam em toda
    // run, e a carência move o instante em que cada compromisso libera para apuração.
    //
    // ⚠️ `["production","active"]` NÃO casa por prefixo com `["production", lineId]` — é a mesma
    // armadilha da run ativa documentada na tabela viva de invalidação. Precisa ser nomeada.
    void qc.invalidateQueries({ queryKey: ["closed-loop", "track-record"] });
    void qc.invalidateQueries({ queryKey: ["production", "active"] });
    // `["applied-scenarios"]` porque a carência move `settlementAvailableAt` de todo compromisso.
    // ⚠️ E o DETALHE tem chave no SINGULAR (`["applied-scenario", id]`, de `useAppliedScenario`),
    // que NÃO casa por prefixo com o plural acima — a mesma armadilha da run ativa, de novo. Sem
    // esta linha, quem estiver com um compromisso aberto continua vendo o `settlementAvailableAt`
    // calculado pela carência antiga: o botão "Apurar" fica cinza sobre um compromisso que a rota
    // já aceita, ou habilitado sobre um que ela recusa com 409.
    void qc.invalidateQueries({ queryKey: ["applied-scenarios"] });
    void qc.invalidateQueries({ queryKey: ["applied-scenario"] });
  };
}

export function useUpdateOrganization() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: (data: UpdateOrganizationRequest) =>
      api.patch<OrganizationIdentity>("/api/v1/organization", data),
    onSuccess: invalidate,
  });
}

export function useUpdateOrganizationCalendar() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: (data: OrganizationCalendarInput) =>
      api.put<OrganizationCalendarResponse>("/api/v1/organization/calendar", data),
    onSuccess: invalidate,
  });
}

export function useDeleteOrganizationCalendar() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: () => api.delete<OrganizationCalendarResponse>("/api/v1/organization/calendar"),
    onSuccess: invalidate,
  });
}

/**
 * Catálogo nacional do ano. `staleTime: Infinity` porque feriado de um ano fechado não muda —
 * refetch aqui é rede gasta para receber a mesma lista.
 */
export function useHolidays(year: number, enabled: boolean) {
  return useQuery<HolidaysResponse>({
    queryKey: ["organization", "holidays", year],
    queryFn: () => api.get<HolidaysResponse>(`/api/v1/organization/holidays?year=${year}`),
    enabled,
    staleTime: Infinity,
  });
}

/**
 * Funde os feriados escolhidos no calendário. O merge é do SERVIDOR — a tela manda o que o
 * gestor manteve marcado, não o calendário inteiro: entre o `GET` que ela usou de base e o envio, o
 * calendário pode ter mudado, e um `PUT` integral apagaria o que ela não viu.
 */
export function useLoadHolidays() {
  const invalidate = useInvalidateOrganization();
  return useMutation({
    mutationFn: (body: LoadHolidaysRequestInput) =>
      api.post<OrganizationCalendarResponse>("/api/v1/organization/calendar/holidays", body),
    onSuccess: invalidate,
  });
}

/**
 * Prévia das próximas ocorrências de uma regra — resolvida PELO SERVIDOR.
 *
 * É `useQuery`, não `useMutation`, apesar de o transporte ser POST: isto é LEITURA (o corpo carrega
 * a regra porque ela não cabe numa query string). Modelá-la como mutation obrigaria cada card de
 * regra a espelhar o resultado em estado local dentro de um efeito — cascata de renders que o
 * próprio lint do React Compiler acusa, e um cache a menos.
 *
 * NÃO INVALIDA NADA: read-model efêmero, o irmão de `usePreviewScenario`.
 */
export function usePreviewOccurrences(
  body: PreviewOccurrencesRequestInput,
  /** Assinatura só das partes que mudam AS DATAS — o motivo da regra não entra. */
  signature: string
) {
  return useQuery<PreviewOccurrencesResponse>({
    queryKey: ["organization", "occurrences", signature],
    queryFn: () =>
      api.post<PreviewOccurrencesResponse>("/api/v1/organization/calendar/occurrences", body),
    // A resposta é função pura da regra: recalcular por foco de janela é rede gasta para receber
    // exatamente as mesmas datas.
    staleTime: Infinity,
    retry: false,
  });
}
